import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { sendChatMessage } from '../../store/slices/chatSlice';
import Button from '../UI/Button';

interface ChatInputProps {
  projectId: string;
  parentMessage?: any; // Message being replied to
  onReplyCancel?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ projectId, parentMessage, onReplyCancel }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { sending } = useAppSelector((state) => state.chat);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sending[projectId] || !user) return;

    try {
      await dispatch(
        sendChatMessage({
          projectId,
          content: message.trim(),
          parentId: parentMessage?.id || parentMessage?._id,
        })
      );
      setMessage('');
      onReplyCancel?.();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  if (!user) {
    return (
      <div className="border-t border-[var(--color-neutral-border)] p-4 bg-[var(--color-neutral-50)] text-center text-sm text-[var(--color-subtext-color)]">
        <p>Please sign in to send messages</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-[var(--color-neutral-border)] p-4 bg-[var(--color-default-background)]">
      {parentMessage && (
        <div className="mb-2 p-2 bg-[var(--color-brand-50)] border border-[var(--color-brand-200)] rounded-lg flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[var(--color-brand-600)] font-medium">Replying to:</p>
            <p className="text-xs text-[var(--color-default-font)] truncate">{parentMessage.content}</p>
          </div>
          <button
            type="button"
            onClick={onReplyCancel}
            className="ml-2 text-[var(--color-subtext-color)] hover:text-[var(--color-default-font)]"
            aria-label="Cancel reply"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={parentMessage ? 'Type a reply...' : 'Type a message...'}
          className="flex-1 px-4 py-2 border border-[var(--color-neutral-border)] rounded-lg bg-[var(--color-default-background)] text-[var(--color-default-font)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)]"
          disabled={sending[projectId]}
          aria-label={parentMessage ? 'Reply input' : 'Chat message input'}
        />
        <Button
          type="submit"
          disabled={!message.trim() || sending[projectId]}
          isLoading={sending[projectId]}
        >
          Send
        </Button>
      </div>
    </form>
  );
};

export default ChatInput;
